import React, { useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { FormGroup, Input, Label } from "reactstrap";
import { useRouterCore } from "../../../cores/router";
import { WebsitePage } from "../../../cores/websitePage/interfaces/WebsitePage";
import { useWebsitePageCore } from "../../../cores/websitePage";
import { websitePageApiService } from "../../../cores/websitePage/services/api";
import { getPreviewPageUrl, resolvePublicPageUrl } from "./websitePageUrls";
import { WebsiteIcon } from "./WebsiteIcon";
import "./PageSettings.scss";

interface Props {
  page: WebsitePage;
  companyId: string;
}

const slugifyPreview = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);

export function PageSettings({ page, companyId }: Props) {
  const intl = useIntl();
  const { goToWebForms } = useRouterCore();
  const { forms, getForms, updateDomain, verifyDomain, updatePage } = useWebsitePageCore();
  const [customDomain, setCustomDomain] = useState(page.customDomain || "");
  const [formId, setFormId] = useState(page.formId || "");
  const [includeWhatsapp, setIncludeWhatsapp] = useState(page.includeWhatsapp);

  const [seoTitle, setSeoTitle] = useState(page.seoTitle || page.name || "");
  const [seoDescription, setSeoDescription] = useState(page.seoDescription || "");
  const [slug, setSlug] = useState(page.slug || "");
  const [faviconUrl, setFaviconUrl] = useState(page.faviconUrl || "");
  const [ogImageUrl, setOgImageUrl] = useState(page.ogImageUrl || "");
  const [isSavingSeo, setIsSavingSeo] = useState(false);
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
  const [isUploadingOg, setIsUploadingOg] = useState(false);

  const faviconInputRef = useRef<HTMLInputElement>(null);
  const ogInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    getForms(companyId);
  }, [companyId, getForms]);

  React.useEffect(() => {
    setSeoTitle(page.seoTitle || page.name || "");
    setSeoDescription(page.seoDescription || "");
    setSlug(page.slug || "");
    setFaviconUrl(page.faviconUrl || "");
    setOgImageUrl(page.ogImageUrl || "");
    setIncludeWhatsapp(page.includeWhatsapp);
    setFormId(page.formId || "");
  }, [page]);

  const handleWhatsappToggle = useCallback(
    async (checked: boolean) => {
      setIncludeWhatsapp(checked);
      try {
        await updatePage(companyId, page.id, { includeWhatsapp: checked });
        toast.success(intl.formatMessage({ id: "app.websitePage.toast.integrationsSaved" }));
      } catch {
        setIncludeWhatsapp(page.includeWhatsapp);
      }
    },
    [companyId, intl, page.id, page.includeWhatsapp, updatePage]
  );

  const handleSaveDomain = useCallback(async () => {
    await updateDomain(companyId, page.id, customDomain || null);
  }, [companyId, customDomain, page.id, updateDomain]);

  const handleVerifyDomain = useCallback(async () => {
    await verifyDomain(companyId, page.id);
  }, [companyId, page.id, verifyDomain]);

  const handleSaveIntegrations = useCallback(async () => {
    await updatePage(companyId, page.id, {
      formId: formId || null,
      includeWhatsapp,
    });
    toast.success(intl.formatMessage({ id: "app.websitePage.toast.integrationsSaved" }));
  }, [companyId, formId, includeWhatsapp, intl, page.id, updatePage]);

  const handleSaveSeo = useCallback(async () => {
    const normalizedSlug = slugifyPreview(slug);
    if (!normalizedSlug) {
      toast.error(intl.formatMessage({ id: "app.websitePage.settings.seo.slugInvalid" }));
      return;
    }

    setIsSavingSeo(true);
    try {
      await updatePage(companyId, page.id, {
        seoTitle: seoTitle.trim() || null,
        seoDescription: seoDescription.trim() || null,
        slug: normalizedSlug,
        faviconUrl: faviconUrl || null,
        ogImageUrl: ogImageUrl || null,
      });
      toast.success(intl.formatMessage({ id: "app.websitePage.settings.seo.saved" }));
    } finally {
      setIsSavingSeo(false);
    }
  }, [companyId, faviconUrl, intl, ogImageUrl, page.id, seoDescription, seoTitle, slug, updatePage]);

  const handleUploadImage = useCallback(
    async (file: File, target: "favicon" | "og") => {
      const setUploading = target === "favicon" ? setIsUploadingFavicon : setIsUploadingOg;
      const setUrl = target === "favicon" ? setFaviconUrl : setOgImageUrl;
      setUploading(true);
      try {
        const uploaded = await websitePageApiService.uploadImage(companyId, file);
        setUrl(uploaded.url);
      } catch {
        toast.error(intl.formatMessage({ id: "app.websitePage.toast.uploadFailed" }));
      } finally {
        setUploading(false);
      }
    },
    [companyId, intl]
  );

  const publicLink = page.publicUrl.startsWith("http")
    ? page.publicUrl
    : page.status === "published"
    ? resolvePublicPageUrl(page.publicUrl)
    : getPreviewPageUrl(companyId, page.id);

  const previewTitle = seoTitle.trim() || page.name;
  const previewDescription =
    seoDescription.trim() ||
    intl.formatMessage({ id: "app.websitePage.settings.seo.previewDescriptionFallback" });
  const previewHost = (() => {
    try {
      return new URL(publicLink).host;
    } catch {
      return window.location.host;
    }
  })();

  return (
    <div className="wp-settings">
      <div className="wp-settings__card">
        <div className="wp-settings__card-header">
          <WebsiteIcon icon="search" />
          <h3>{intl.formatMessage({ id: "app.websitePage.settings.seo.title" })}</h3>
        </div>
        <div className="wp-settings__card-body">
          <div className="wp-settings__info">
            <WebsiteIcon icon="info-circle" />
            <div>{intl.formatMessage({ id: "app.websitePage.settings.seo.help" })}</div>
          </div>

          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.settings.seo.pageTitle" })}</Label>
            <Input
              className="wp-input-compat"
              value={seoTitle}
              onChange={(e) => setSeoTitle(e.target.value)}
              placeholder={page.name}
              maxLength={256}
            />
          </FormGroup>

          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.settings.seo.description" })}</Label>
            <Input
              className="wp-input-compat"
              type="textarea"
              rows={3}
              value={seoDescription}
              onChange={(e) => setSeoDescription(e.target.value)}
              placeholder={intl.formatMessage({ id: "app.websitePage.settings.seo.descriptionPlaceholder" })}
              maxLength={512}
            />
            <small className="wp-settings__char-count">{seoDescription.length}/512</small>
          </FormGroup>

          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.settings.seo.slug" })}</Label>
            <div className="wp-settings__slug-row">
              <span className="wp-settings__slug-prefix">/</span>
              <Input
                className="wp-input-compat"
                value={slug}
                onChange={(e) => setSlug(slugifyPreview(e.target.value))}
                placeholder="mi-pagina"
                maxLength={80}
              />
            </div>
            <small className="wp-settings__hint">
              {intl.formatMessage({ id: "app.websitePage.settings.seo.slugHint" })}
            </small>
          </FormGroup>

          <div className="wp-settings__upload-grid">
            <FormGroup>
              <Label>{intl.formatMessage({ id: "app.websitePage.settings.seo.favicon" })}</Label>
              <input
                ref={faviconInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/x-icon,image/vnd.microsoft.icon,.ico"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUploadImage(file, "favicon");
                }}
              />
              <div className="wp-settings__upload-row">
                <button
                  type="button"
                  className="wp-btn wp-btn--secondary wp-btn--sm"
                  onClick={() => faviconInputRef.current?.click()}
                  disabled={isUploadingFavicon}
                >
                  <WebsiteIcon icon="upload" />
                  {isUploadingFavicon
                    ? intl.formatMessage({ id: "app.websitePage.settings.seo.uploading" })
                    : intl.formatMessage({ id: "app.websitePage.settings.seo.uploadFavicon" })}
                </button>
                {faviconUrl && (
                  <img src={faviconUrl} alt="" className="wp-settings__favicon-preview" />
                )}
              </div>
            </FormGroup>

            <FormGroup>
              <Label>{intl.formatMessage({ id: "app.websitePage.settings.seo.ogImage" })}</Label>
              <input
                ref={ogInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUploadImage(file, "og");
                }}
              />
              <div className="wp-settings__upload-row">
                <button
                  type="button"
                  className="wp-btn wp-btn--secondary wp-btn--sm"
                  onClick={() => ogInputRef.current?.click()}
                  disabled={isUploadingOg}
                >
                  <WebsiteIcon icon="upload" />
                  {isUploadingOg
                    ? intl.formatMessage({ id: "app.websitePage.settings.seo.uploading" })
                    : intl.formatMessage({ id: "app.websitePage.settings.seo.uploadOgImage" })}
                </button>
              </div>
              <small className="wp-settings__hint">
                {intl.formatMessage({ id: "app.websitePage.settings.seo.ogImageHint" })}
              </small>
            </FormGroup>
          </div>

          <div className="wp-settings__link-preview">
            <div className="wp-settings__link-preview-label">
              <WebsiteIcon icon="share-alt" />
              {intl.formatMessage({ id: "app.websitePage.settings.seo.linkPreview" })}
            </div>
            <div className="wp-settings__link-preview-card">
              {ogImageUrl ? (
                <div
                  className="wp-settings__link-preview-image"
                  style={{ backgroundImage: `url(${ogImageUrl})` }}
                />
              ) : (
                <div className="wp-settings__link-preview-image wp-settings__link-preview-image--empty">
                  <WebsiteIcon icon="image" />
                  <span>{intl.formatMessage({ id: "app.websitePage.settings.seo.noPreviewImage" })}</span>
                </div>
              )}
              <div className="wp-settings__link-preview-body">
                <span className="wp-settings__link-preview-host">{previewHost}</span>
                <strong className="wp-settings__link-preview-title">{previewTitle}</strong>
                <p className="wp-settings__link-preview-desc">{previewDescription}</p>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="wp-btn wp-btn--primary wp-btn--sm"
            onClick={handleSaveSeo}
            disabled={isSavingSeo}
          >
            {isSavingSeo
              ? intl.formatMessage({ id: "app.websitePage.saving" })
              : intl.formatMessage({ id: "app.websitePage.settings.seo.save" })}
          </button>
        </div>
      </div>

      <div className="wp-settings__card">
        <div className="wp-settings__card-header">
          <WebsiteIcon icon="link" />
          <h3>{intl.formatMessage({ id: "app.websitePage.settings.publicLink" })}</h3>
        </div>
        <div className="wp-settings__card-body">
          <p className="wp-settings__hint">{intl.formatMessage({ id: "app.websitePage.settings.publicLinkHint" })}</p>
          <div className="wp-settings__link-row">
            <input className="wp-input" readOnly value={publicLink} />
            <button
              type="button"
              className="wp-btn wp-btn--secondary wp-btn--sm"
              aria-label={intl.formatMessage({ id: "app.websitePage.settings.copyLink" })}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(publicLink);
                  toast.success(intl.formatMessage({ id: "app.websitePage.toast.linkCopied" }));
                } catch {
                  toast.error(intl.formatMessage({ id: "app.websitePage.toast.copyFailed" }));
                }
              }}
            >
              <WebsiteIcon icon="copy" />
            </button>
          </div>
        </div>
      </div>

      <div className="wp-settings__card">
        <div className="wp-settings__card-header">
          <WebsiteIcon icon="globe" />
          <h3>{intl.formatMessage({ id: "app.websitePage.settings.domain" })}</h3>
        </div>
        <div className="wp-settings__card-body">
          <div className="wp-settings__info">
            <WebsiteIcon icon="info-circle" />
            <div>
              {intl.formatMessage({ id: "app.websitePage.settings.dnsInstructions" })}
              <code>CNAME → {process.env.REACT_APP_WEBSITE_PAGES_CNAME_TARGET || "pages.wembii.com"}</code>
            </div>
          </div>
          <FormGroup>
            <Label>{intl.formatMessage({ id: "app.websitePage.settings.customDomain" })}</Label>
            <Input
              className="wp-input-compat"
              value={customDomain}
              placeholder="www.tuempresa.com"
              onChange={(e) => setCustomDomain(e.target.value)}
            />
          </FormGroup>
          <div className="wp-settings__actions">
            <button type="button" className="wp-btn wp-btn--primary wp-btn--sm" onClick={handleSaveDomain}>
              {intl.formatMessage({ id: "app.websitePage.settings.saveDomain" })}
            </button>
            {customDomain && (
              <button type="button" className="wp-btn wp-btn--secondary wp-btn--sm" onClick={handleVerifyDomain}>
                {intl.formatMessage({ id: "app.websitePage.settings.verifyDomain" })}
              </button>
            )}
          </div>
          {page.customDomainVerified && (
            <div className="wp-settings__success">
              <WebsiteIcon icon="check-circle" />
              {intl.formatMessage({ id: "app.websitePage.settings.domainVerified" })}
            </div>
          )}
        </div>
      </div>

      <div className="wp-settings__card">
        <div className="wp-settings__card-header">
          <WebsiteIcon icon="plug" />
          <h3>{intl.formatMessage({ id: "app.websitePage.settings.integrations" })}</h3>
        </div>
        <div className="wp-settings__card-body">
          <label className="wp-settings__toggle">
            <input
              type="checkbox"
              checked={includeWhatsapp}
              onChange={(e) => handleWhatsappToggle(e.target.checked)}
            />
            <span className="wp-settings__toggle-track" />
            <span className="wp-settings__toggle-label">
              <i className="fab fa-whatsapp" />
              {intl.formatMessage({ id: "app.websitePage.settings.whatsapp" })}
            </span>
          </label>

          <FormGroup className="mt-3">
            <Label>{intl.formatMessage({ id: "app.websitePage.settings.contactForm" })}</Label>
            <Input className="wp-input-compat" type="select" value={formId} onChange={(e) => setFormId(e.target.value)}>
              <option value="">{intl.formatMessage({ id: "app.websitePage.settings.noForm" })}</option>
              {forms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.name}
                </option>
              ))}
            </Input>
            {forms.length === 0 && (
              <button type="button" className="wp-settings__link-btn" onClick={goToWebForms}>
                {intl.formatMessage({ id: "app.websitePage.settings.createForm" })}
              </button>
            )}
          </FormGroup>

          <button type="button" className="wp-btn wp-btn--primary wp-btn--sm" onClick={handleSaveIntegrations}>
            {intl.formatMessage({ id: "app.websitePage.settings.saveIntegrations" })}
          </button>
        </div>
      </div>
    </div>
  );
}
